import { useEffect, useMemo, useState } from 'react'
import { AspenCandidatesService, StatusOptionDTO } from '../client'

/**
 * Status dropdown options, plus the subset the backend marks actionable.
 *
 * ``actionable`` is owned by ACTIONABLE_STATUSES on the server, so the worklist
 * default and the status enum cannot drift. Nothing here hard-codes which
 * statuses count as live work.
 */
export const useStatusOptions = () => {
    const [options, setOptions] = useState<StatusOptionDTO[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let isMounted = true

        const fetchStatusOptions = async () => {
            setLoading(true)
            setError(null)
            try {
                const data = await AspenCandidatesService.getStatusOptionsCandidatesStatusOptionsGet()

                if (isMounted && data?.statuses) {
                    setOptions(data.statuses)
                }
            } catch (err) {
                if (isMounted) {
                    setError('Failed to fetch status options')
                    console.error('Failed to fetch status options:', err)
                    // Deliberately no hard-coded fallback. A stale list would be
                    // rejected by the backend's status enum with a 422, and a
                    // guessed actionable set would silently hide candidates the
                    // coordinator needs to see.
                    setOptions([])
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        fetchStatusOptions()

        return () => {
            isMounted = false
        }
    }, [])

    const statusOptions = useMemo(() => options.map((option) => option.value), [options])
    const actionableStatuses = useMemo(() => options.filter((option) => option.actionable).map((option) => option.value), [options])

    return { statusOptions, actionableStatuses, options, loading, error }
}
